import dayjs from 'dayjs';
import { z } from 'zod';

export const ymdDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');

export const analyticsUpdateRequestBoundsSchema = z.object({
  today: ymdDateSchema,
  earliestFrom: ymdDateSchema,
  timezoneCount: z.number().int().nonnegative(),
  maxDays: z.number().int().positive(),
});

export type AnalyticsUpdateRequestBounds = z.infer<typeof analyticsUpdateRequestBoundsSchema>;

export const analyticsUpdateRequestResultSchema = z.object({
  from: ymdDateSchema,
  to: ymdDateSchema,
  londonRecords: z.number().int().nonnegative().optional(),
  timezoneRecords: z.number().int().nonnegative().optional(),
  totalRecords: z.number().int().nonnegative().optional(),
});

export type AnalyticsUpdateRequestResult = z.infer<typeof analyticsUpdateRequestResultSchema>;

export const analyticsUpdateRequestErrorSchema = z.object({
  code: z.enum([
    'LOOKBACK_NOT_CONFIGURED',
    'INVALID_RANGE',
    'RANGE_NO_LONGER_AVAILABLE',
    'CALENDAR_DATES_MISSING',
  ]),
  message: z.string(),
  earliestFrom: ymdDateSchema.optional(),
  today: ymdDateSchema.optional(),
});

export type AnalyticsUpdateRequestError = z.infer<typeof analyticsUpdateRequestErrorSchema>;

export const requestAnalyticsUpdateFormValuesSchema = z.object({
  from: ymdDateSchema,
  to: ymdDateSchema,
});

export type RequestAnalyticsUpdateFormValues = z.infer<
  typeof requestAnalyticsUpdateFormValuesSchema
>;

export type AnalyticsUpdateRequestDateBounds = Pick<
  AnalyticsUpdateRequestBounds,
  'earliestFrom' | 'today'
>;

export function createRequestAnalyticsUpdateFormSchema(bounds: AnalyticsUpdateRequestDateBounds) {
  return requestAnalyticsUpdateFormValuesSchema.superRefine((value, ctx) => {
    if (value.to < value.from) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['to'],
        message: 'To date cannot be earlier than from date',
      });
    }
    if (value.from < bounds.earliestFrom) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['from'],
        message: `Cannot be earlier than ${formatDisplayDate(bounds.earliestFrom)}`,
      });
    }
    if (value.from > bounds.today) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['from'],
        message: 'From date cannot be in the future',
      });
    }
    if (value.to > bounds.today) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['to'],
        message: 'To date cannot be in the future',
      });
    }
  });
}

/** Failed refetches keep cached `data`; only use it when the refetch itself succeeded. */
export function boundsFromRefetchResult(result: {
  isError: boolean;
  data?: AnalyticsUpdateRequestBounds;
}): AnalyticsUpdateRequestBounds | undefined {
  if (result.isError) return undefined;
  return result.data;
}

export function isRangeWithinBounds(
  values: RequestAnalyticsUpdateFormValues,
  bounds: AnalyticsUpdateRequestDateBounds,
): boolean {
  return (
    values.from >= bounds.earliestFrom &&
    values.from <= bounds.today &&
    values.to >= values.from &&
    values.to <= bounds.today
  );
}

export function rangeChangedMessage(earliestFrom?: string): string {
  if (!earliestFrom) {
    return 'The available range has changed. Adjust the dates and try again.';
  }
  return `The available range has changed. Analytics updates can now only be requested from ${formatDisplayDate(
    earliestFrom,
  )} onward. Adjust the dates and try again.`;
}

export function inclusiveDayCount(from: string, to: string): number {
  return dayjs(to).diff(dayjs(from), 'day') + 1;
}

export function computeUpdateCounts(
  from: string,
  to: string,
  timezoneCount: number,
): Pick<AnalyticsUpdateRequestResult, 'londonRecords' | 'timezoneRecords' | 'totalRecords'> {
  const londonRecords = inclusiveDayCount(from, to);
  const timezoneRecords = londonRecords * timezoneCount;
  return {
    londonRecords,
    timezoneRecords,
    totalRecords: londonRecords + timezoneRecords,
  };
}

export function formatDisplayDate(ymd: string): string {
  return dayjs(ymd).format('MMMM D, YYYY');
}

export function formatDateRangeLabel(from: string, to: string): string {
  if (from === to) {
    return formatDisplayDate(from);
  }
  const fromDay = dayjs(from);
  const toDay = dayjs(to);
  if (fromDay.isSame(toDay, 'year') && fromDay.isSame(toDay, 'month')) {
    return `${fromDay.format('MMMM D')}–${toDay.format('D')}`;
  }
  if (fromDay.isSame(toDay, 'year')) {
    return `${fromDay.format('MMMM D')}–${toDay.format('MMMM D')}`;
  }
  return `${formatDisplayDate(from)} – ${formatDisplayDate(to)}`;
}

export function availableRangeHelperText(earliestFrom: string): string {
  return `Analytics updates can be requested from ${formatDisplayDate(earliestFrom)} onward.`;
}

export const ESTIMATED_COUNTS_NOTE =
  'Counts are estimates. Some timezone records for today may not exist yet.';

export type DateRangePreset = {
  id: string;
  label: string;
  from: string;
  to: string;
};

export function clampedRangePresets(bounds: AnalyticsUpdateRequestDateBounds): DateRangePreset[] {
  const today = dayjs(bounds.today);
  const clampFrom = (value: dayjs.Dayjs) => {
    const ymd = value.format('YYYY-MM-DD');
    if (ymd < bounds.earliestFrom) return bounds.earliestFrom;
    if (ymd > bounds.today) return bounds.today;
    return ymd;
  };

  return [
    { id: 'today', label: 'Today', from: bounds.today, to: bounds.today },
    {
      id: 'lastSevenDays',
      label: 'Last 7 days',
      from: clampFrom(today.subtract(6, 'day')),
      to: bounds.today,
    },
    {
      id: 'lastThirtyDays',
      label: 'Last 30 days',
      from: clampFrom(today.subtract(29, 'day')),
      to: bounds.today,
    },
    {
      id: 'thisMonth',
      label: 'This month',
      from: clampFrom(today.startOf('month')),
      to: bounds.today,
    },
    {
      id: 'allAvailable',
      label: 'All available',
      from: bounds.earliestFrom,
      to: bounds.today,
    },
  ];
}
